<template>
  <div>
    <Subheader title="Profile Setup" />

    <form class="app-content-column" @submit.stop.prevent="handleSubmit">
      <div class="card">
        <h5>Your Mzingo Partners Community Profile</h5>
        <p>
          This is what other registered Mzingo Partners users will be able to see about you. Let
          them know what matters to you and how you want to help change the world.
        </p>
      </div>

      <FormError v-if="showFormErrors" :form-errors="formErrors" />

      <div class="card">
        <h5>About You</h5>
        <div class="form">
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="description">
            <textarea
              v-model="newUserData.description"
              placeholder="Your Bio"
              class="textarea"
              required
            />
          </Field>

          <h5>Select Profile Picture</h5>
          <input ref="resultFiles" type="file" @change="previewFile" />
        </div>
      </div>

      <div class="card">
        <h5>Social Media</h5>
        <div class="form">
          <Field
            :errors="fieldErrors"
            :show-field-errors="showFieldErrors"
            :form-errors="formErrors"
            name="facebook"
          >
            <input v-model="newUserData.facebook" placeholder="Facebook" />
          </Field>

          <Field
            :errors="fieldErrors"
            :show-field-errors="showFieldErrors"
            :form-errors="formErrors"
            name="instagram"
          >
            <input v-model="newUserData.instagram" placeholder="Instagram" />
          </Field>

          <Field
            :errors="fieldErrors"
            :show-field-errors="showFieldErrors"
            :form-errors="formErrors"
            name="twitter"
          >
            <input v-model="newUserData.twitter" placeholder="Twitter" />
          </Field>
        </div>
      </div>

      <div class="card">
        <h5>Causes and Locations</h5>
        <p>What are the causes and locations about which you care the most?</p>

        <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="selectedAttrs">
          <h4>Causes</h4>
          <div class="button-wrapper">
            <div
              v-for="attr in interestAttrs"
              :key="attr.id"
              type="button"
              class="tag"
              :class="{ 'tag--inverted': attr.selected }"
              @click="toggleAttributes(attr)"
            >
              {{ attr.name }}
            </div>
          </div>

          <h4>Locations</h4>
          <div class="button-wrapper">
            <div
              v-for="attr in locationAttrs"
              :key="attr.id"
              type="button"
              class="tag"
              :class="{ 'tag--inverted': attr.selected }"
              @click="toggleAttributes(attr)"
            >
              {{ attr.name }}
            </div>
          </div>
        </Field>
      </div>

      <div class="button-wrapper is-centered">
        <button class="large-button" type="submit" :disabled="loading">
          Next
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Subheader from '@/components/Subheader'
import Field from '@/components/Field'
import FormError from '@/components/FormError'

import Attribute from '@/services/attributes'
import User from '@/services/users'
import { validateInputLength, validateSelectMinimum } from '@/services/forms'

export default {
  name: 'ProfileSetup',
  components: {
    Subheader,
    Field,
    FormError,
  },
  data() {
    return {
      user: null,
      newUserData: {
        description: '',
        facebook: '',
        instagram: '',
        twitter: '',
        selectedAttrs: [],
        photo: null,
      },
      locationAttrs: [],
      interestAttrs: [],
      fieldErrors: [],
      showFieldErrors: false,
      formErrors: {},
      showFormErrors: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['getUser']),
  },
  mounted() {
    this.getAttributes()
    this.user = this.getUser
  },
  methods: {
    validateInputLength,
    validateSelectMinimum,
    previewFile(event) {
      this.newUserData.photo = event.target.files[0]
    },
    async getAttributes() {
      this.loading = true
      let attrList = await Attribute.api.list()
      attrList = attrList.results.map(attr => ({ ...attr, selected: false }))
      this.locationAttrs = attrList.filter(attr => attr.group === 'Location')
      this.interestAttrs = attrList.filter(attr => attr.group === 'Interest Area')
      this.loading = false
    },
    /* Toggle attribute selection */
    toggleAttributes(attr) {
      if (attr.selected) {
        const remainingAttrs = this.newUserData.selectedAttrs.filter(e => {
          return e !== attr.id
        })
        this.newUserData.selectedAttrs = remainingAttrs
        attr.selected = false
      } else if (!attr.selected) {
        this.newUserData.selectedAttrs.push(attr.id)
        attr.selected = true
      }
    },
    async handleSubmit() {
      this.loading = true
      this.fieldErrors = this.collectFieldErrors()

      if (this.fieldErrors.length) {
        this.showFieldErrors = true
      } else {
        try {
          const fields = ['description', 'interests', 'facebook', 'instagram', 'twitter']
          this.user.interests = this.newUserData.selectedAttrs
          this.user.description = this.newUserData.description
          this.user.facebook = this.newUserData.facebook
          this.user.instagram = this.newUserData.instagram
          this.user.twitter = this.newUserData.twitter
          let response = null

          if (this.newUserData.photo) {
            response = await User.api.uploadProfilePic(this.user.id, this.newUserData.photo)
          }

          response = await User.api.update(this.user, fields)
          this.$store.commit('UPDATE_USER', response)
          this.$router.push({ name: 'Home' })
        } catch (error) {
          const { response } = error
          if (response && response.data) {
            this.formErrors = response.data
            this.showFormErrors = true
          }
          this.loading = false
          return
        }
      }
      this.loading = false
    },
    collectFieldErrors() {
      //collecting all field errors - validating length
      //exception(not required): photo
      const errors = []

      Object.keys(this.newUserData).forEach(field => {
        if (field === 'description') {
          const error = validateInputLength(field, this.newUserData[field])
          if (error) {
            errors.push(error)
          }
        }
        if (field === 'selectedAttrs') {
          const error = validateSelectMinimum(field, this.newUserData[field])
          if (error) {
            errors.push(error)
          }
        }
      })

      return errors
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
}
</style>
